import {ServiceEventTicketPolicyEntity} from "@ots-frontend/ots-data";
import {Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import { EntityAnchor } from "../../anchor/EntityAnchor/EntityAnchor";
import {FormattedQuantity} from "@ots-frontend/ots-ui";

export type ServiceEventTicketPolicyDetailsProps = {
  policy: ServiceEventTicketPolicyEntity
}

export const ServiceEventTicketPolicyDetails = (props: ServiceEventTicketPolicyDetailsProps) => {
  const {policy} = props;

  const {t} = useTranslation(["common"]);

  return (
    <Grid>
      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.serviceeventticketpolicy.info.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {policy.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.dnu.label", "Do Not Use")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {policy.doNotUse ? "Yes" : "No"}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.desc.label", "Description")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={policy.description} defaultValue={"No description provided."}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider my="xs" variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.type.label", "Event Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={policy.eventType}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.company.label", "Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <EntityAnchor value={{
                  objectId: policy.companyId,
                  objectType: "COMPANY",
                  objectName: policy.companyName
                }}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.servicetype.label", "Service Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <EntityAnchor value={{
                  objectId: policy.serviceTypeId,
                  objectType: "SERVICETYPE",
                  objectName: policy.serviceTypeName
                }}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider my="xs" variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.triggerfacet.label", "Trigger Facet")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {policy.triggerFacet}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.triggerfromstatus.label", "Trigger From Status")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {policy.triggerFromStatus}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.triggertostatus.label", "Trigger To Status")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {policy.triggerToStatus}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.minseverity.label", "Min. Severity")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={policy.minSeverity} defaultValue={"No minimum severity set."}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider my="xs" variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.category.label", "Category")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
               <FormattedDatabaseCode value={policy.category}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.freighttype.label", "Freight Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={policy.freightTypeName} />
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.triggertostatus.label", "Trigger To Status")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {policy.triggerToStatus}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.serviceeventticketpolicy.info.field.defaultquantity.label", "Default Quantity")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {typeof policy.defaultQuantity === "undefined" ? "Not set." : ( <FormattedQuantity value={{ number: policy.defaultQuantity, units: policy.defaultQuantityUom }} />)}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>
    </Grid>
  );
};

